import useSWR from 'swr';
import { getAuthenticatedHeaders } from './fetchHeaders';
import useSWRInfinite from 'swr/infinite';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

export const useVeryRecentSnapshots = ({ isEnabled, pageSize, minutes }) => {
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        isEnabled ? `/api/v1/servicelog/snapshots/recent?pageIndex=0&pageSize=${pageSize}&minutes=${minutes}` : null,
        jsonFetcher,
        swrConfig);

    return { items: data?.items || [], error, isValidating, isLoading, mutate };
}

export const useRecentSnapshots = ({ pageSize }) => {
    const {
        serviceLogSearchTextBoxValue,
    } = useSelector((state) => state.libraryFilters);
    const [query, _callback] = useDebounce(serviceLogSearchTextBoxValue, 750);

    if (!pageSize) {
        pageSize = 10;
    }
    const { data, error, isLoading, isValidating, mutate, size, setSize } = useSWRInfinite(
        (pageIndex, previousPageData) => getRecentSnapshotKey(pageIndex, previousPageData, { query, pageSize }),
        jsonFetcher,
        { ...swrConfig, initialSize: 1, revalidateFirstPage: false });

    let totalRecords = 0;
    let items = [];

    if (!!data) {
        totalRecords = data[0].totalRecords;
        items = data.reduce((accumulator, currentValue) => accumulator.concat(currentValue.items), []);
    }

    return { items, totalRecords, error, isValidating, isLoading, mutate, size, setSize };
}

const getRecentSnapshotKey = (pageIndex, previousPageData, listParams) => {
    const { pageSize, query } = listParams;

    const path = `/api/v1/servicelog/snapshots/recent`;
    const params = new URLSearchParams('')
    params.set('pageSize', pageSize);
    params.set('pageIndex', pageIndex);

    if (!!query) {
        params.set('query', query);
    }

    if (previousPageData && !previousPageData.items.length) {
        return null; // reached the end
    }

    return `${path}?${params.toString()}`;
}

export const useRecentServiceLog = ({ pageSize }) => {
    const {
        serviceLogSearchTextBoxValue,
    } = useSelector((state) => state.libraryFilters);
    const [query, _callback] = useDebounce(serviceLogSearchTextBoxValue, 750);

    if (!pageSize) {
        pageSize = 10;
    }
    const { data, error, isLoading, isValidating, mutate, size, setSize } = useSWRInfinite(
        (pageIndex, previousPageData) => getRecentServiceKey(pageIndex, previousPageData, { query, pageSize }),
        jsonFetcher,
        { ...swrConfig, initialSize: 1, revalidateFirstPage: false });

    let totalRecords = 0;
    let items = [];

    if (!!data) {
        totalRecords = data[0].totalRecords;
        items = data.reduce((accumulator, currentValue) => accumulator.concat(currentValue.items), []);
    }

    return { items, totalRecords, error, isValidating, isLoading, mutate, size, setSize };
}

const getRecentServiceKey = (pageIndex, previousPageData, listParams) => {
    const { pageSize, query } = listParams;

    const path = `/api/v1/servicelog/list`;
    const params = new URLSearchParams('')
    params.set('pageSize', pageSize);
    params.set('pageIndex', pageIndex);

    if (!!query) {
        params.set('query', query);
    }

    if (previousPageData && !previousPageData.items.length) {
        return null; // reached the end
    }

    return `${path}?${params.toString()}`;
}

export const useGetDefaultServiceLog = (sourceSnapshotId) => {
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        !!sourceSnapshotId ? `/api/v1/servicelog/item/add?sourceSnapshotId=${sourceSnapshotId}` : '/api/v1/servicelog/item/add',
        jsonFetcher,
        swrConfig);

    return { data, error, isValidating, isLoading, mutate };
}

export const useGetServiceLog = (id) => {
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        `/api/v1/servicelog/item/${id}`,
        jsonFetcher,
        swrConfig);

    return { data, error, isValidating, isLoading, mutate };
}

export const useServiceTypes = (serialNumbers) => {
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        !serialNumbers ? `/api/v1/servicelog/serviceTypes` : `/api/v1/servicelog/serviceTypes?serialNumbers=${encodeURIComponent(serialNumbers)}`,
        jsonFetcher,
        swrConfig);

    return { data: data || [], error, isValidating, isLoading, mutate };
}

const jsonFetcher = (url) => {
    return getAuthenticatedHeaders().then((headers) => {
        return fetch(url, { headers })
            .then(response => response.json());
    });
}

const swrConfig = {
    revalidateOnFocus: false,
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        // Never retry on 4XX error.
        if (error.status >= 400 && error.status < 500) return;

        //   // Never retry for a specific key.
        //   if (key === '/api/user') return

        // Only retry up to 1 times.
        if (retryCount > 1) return;

        // Retry after 1 seconds.
        setTimeout(() => { revalidate({ retryCount }); }, 1000)
    }
};